import React, { useState } from "react";
import {
  EMPLOYEE_API_03,
  USERS_API_07,
  USERS_API_11,
} from "../../api/ApiModel"; // Add API for verifying old password
import {
  externalgetApi,
  externalpostApi,
} from "../../services/external/externalApis";
import { useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import AppBar from "@mui/material/AppBar";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Navbar from "../../components/common/Navbar";
import { useStyles } from "../../assets/styles/Theme";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";

const ChangePassword = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const styles = useStyles(theme);

  // Get user parameters from Redux store
  const SYS_USER = useSelector((state) => state.user) || {};
  const { COMPCODE, USERREFCODE, USERNAME, STOREDATE, SETUPDATE, SYSDATE } =
    SYS_USER.USER_PARAM || {};
  const goToPage = () => {
    navigate("/");
  };

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");

  // Toggle password visibility
  const handleTogglePasswordVisibility = (setShow) => {
    setShow((show) => !show);
  };

  // Check password strength and enforce validation rules
  const checkPasswordStrength = (password) => {
    if (password.length === 0) return "";
    if (password.length < 3) return "Weak";

    const strongRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])/;
    if (strongRegex.test(password)) return "Strong";

    return "Medium";
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    setPasswordStrength(checkPasswordStrength(value));
  };

  const validateForm = () => {
    if (oldPassword.length === 0) {
      setErrorMessage("Old password is required.");
      return false;
    }
    if (newPassword.length < 3) {
      setErrorMessage("New password must be at least 8 characters.");
      return false;
    }
    if (passwordStrength === "Weak") {
      setErrorMessage("New password is too weak.");
      return false;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage("New password and confirm password do not match.");
      return false;
    }
    return true;
  };

  // API call to verify the old password
  const verifyOldPassword = async () => {
    try {
      const response = await externalgetApi(USERS_API_11, "GET", {
        USERREFCODE: String(USERREFCODE),
        OLDPASSWORD: String(oldPassword),
      });

      console.log("API Response verifyOldPassword:", response);

      // Assuming the response contains a direct boolean (true/false)
      if (response === true) {
        console.log("Old password verified successfully.");
        return true;
      } else if (response === false) {
        setErrorMessage("Old password is incorrect.");
        return false;
      } else {
        setErrorMessage("Unexpected response from the server.");
        return false;
      }
    } catch (error) {
      console.error("Error verifying old password:", error);
      setErrorMessage("Error verifying old password.");
      return false;
    }
  };

  // API call to update the password
  const updatePassword = async () => {
    try {
      const today = new Date();
      today.setMonth(today.getMonth() + 6);
      const expdate = today.toISOString().split("T")[0];

      const response = await externalpostApi(USERS_API_07, {
        COMPCODE,
        USERREFCODE: String(USERREFCODE),
        USERNAME,
        reason: "Password change",
        password: String(newPassword),
        expdate,
        locdate: SYSDATE,
        enuser: USERNAME,
      });

      console.log("API Response (updatePassword):", response);

      // Assuming a truthy response indicates success
      if (response) {
        setSuccessMessage("Password Updated Successfully!");
        setErrorMessage(""); // Clear error message on success
      } else {
        setErrorMessage("Password update failed");
      }
    } catch (error) {
      console.error("Error updating password:", error);
      setErrorMessage("Password update failed");
    }
  };

  // Handle password change process
  // Handle password change process
  const handlePasswordChange = async () => {
    setErrorMessage(""); // Clear any previous errors
    setSuccessMessage("");

    // Validate form before submitting
    if (!validateForm()) return;

    // Verify the old password first
    const isOldPasswordCorrect = await verifyOldPassword();
    if (!isOldPasswordCorrect) return;

    // If old password is verified and passwords match, proceed with updating the password
    await updatePassword();
  };

  return (
    <div>
      <Navbar />
      <AppBar sx={styles.AI_CustomAppBar1}>
        <Box sx={styles.AI_HeaderContainer}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Breadcrumbs
                sx={styles.AI_breadcrumbs2}
                position="fixed"
                separator={<NavigateNextIcon fontSize="small" />}
              >
                <Link underline="none">Home</Link>
                <Link underline="none">Users</Link>
                <Link underline="none">Change Password</Link>
              </Breadcrumbs>
            </Grid>
          </Grid>
        </Box>
      </AppBar>

      <Box sx={styles.cp_changepwd}>
        <Box sx={styles.cp_changepwd_container}>
          <Box mb={2} textAlign={"center"}>
            <Typography variant="title">Change Password</Typography>
          </Box>

          <Box>
            <TextField
              label="Current Password"
              size="small"
              fullWidth
              variant="outlined"
              type={showOldPassword ? "text" : "password"}
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              autoComplete="off"
              InputLabelProps={{ sx: { ...theme.typography.body2 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        handleTogglePasswordVisibility(setShowOldPassword)
                      }
                      edge="end"
                    >
                      {showOldPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {errorMessage && (
              <Typography variant="body2" color="red" pt={"2px"}>
                {errorMessage}
              </Typography>
            )}
          </Box>

          <Box pt={2}>
            <TextField
              label="New Password"
              size="small"
              fullWidth
              variant="outlined"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={handleNewPasswordChange}
              autoComplete="off"
              InputLabelProps={{ sx: { ...theme.typography.body2 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        handleTogglePasswordVisibility(setShowNewPassword)
                      }
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {/* Password strength indicator */}
            <Typography
              pt={"2px"}
              variant="body2"
              color={
                passwordStrength === "Strong"
                  ? "green"
                  : passwordStrength === "Weak"
                  ? "red"
                  : "orange"
              }
            >
              {passwordStrength && `Password Strength: ${passwordStrength}`}
            </Typography>
          </Box>

          <Box pt={2}>
            <TextField
              label="Confirm New Password"
              size="small"
              fullWidth
              variant="outlined"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="off"
              InputLabelProps={{ sx: { ...theme.typography.body2 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        handleTogglePasswordVisibility(setShowConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {successMessage && (
            <Typography color="primary">{successMessage}</Typography>
          )}
          <Box display={"flex"} pt={2} gap={2}>
            <Button
              variant="outlined"
              size="medium"
              sx={styles.btn_secondary}
              onClick={() => {
                setOldPassword("");
                setNewPassword("");
                setConfirmPassword("");
                setErrorMessage("");
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              sx={styles.btn_primary}
              onClick={handlePasswordChange}
              size="medium"
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export default ChangePassword;
